import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import addIconGold from "@/assets/Add-icon-gold.svg";
import UserChatBubble from "@/Components/Forms/FirmUserBtn";
import { formatDate } from "@/Utils/helper";

import api from "../../../api/api";
import SmartResponsiveTabsContainer from "../../DefendantPage/DefandantMain/components/ResponsiveTabFirmSettings";

import "./account.css";

const ROOT = "/api/account-page";

const errorMessage = (error) => {
  if (error?.response?.status === 502) {
    return "The billing provider is temporarily unavailable. Nothing was changed; please try again.";
  }

  if (error?.response?.status === 403) {
    return (
      error.response?.data?.detail ||
      "Only the firm owner or an account administrator can access Account settings."
    );
  }

  return error?.response?.data?.detail || error?.message || "Something went wrong.";
};

const money = (value, currency = "USD") => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) return "—";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);
};

const date = (value) =>
  value
    ? new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
      }).format(new Date(value))
    : "—";

let stripeLoader;

const loadStripeJs = () => {
  if (window.Stripe) {
    return Promise.resolve(window.Stripe);
  }

  if (stripeLoader) {
    return stripeLoader;
  }

  stripeLoader = new Promise((resolve, reject) => {
    const script = document.createElement("script");

    script.src = "https://js.stripe.com/v3/";
    script.async = true;

    script.onload = () => resolve(window.Stripe);

    script.onerror = () => {
      stripeLoader = null;
      reject(new Error("Stripe.js could not be loaded."));
    };

    document.head.appendChild(script);
  });

  return stripeLoader;
};

function Alert({ message, tone = "error" }) {
  if (!message) return null;

  return <div className={`account-alert account-alert-${tone}`}>{message}</div>;
}

function PaymentSetup({ onSaved, onCancel }) {
  const mountRef = useRef(null);
  const paymentElement = useRef(null);
  const stripe = useRef(null);
  const elements = useRef(null);
  const setupIntentId = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

        if (!key) {
          throw new Error("Stripe publishable key is not configured.");
        }

        const [{ data }, Stripe] = await Promise.all([
          api.post(`${ROOT}/payment-methods/setup-intent/`, {}),
          loadStripeJs(),
        ]);

        if (cancelled || !mountRef.current) {
          return;
        }

        stripe.current = Stripe(key);

        setupIntentId.current = data.setup_intent_id;

        elements.current = stripe.current.elements({
          clientSecret: data.client_secret,

          appearance: {
            theme: "stripe",

            variables: {
              colorPrimary: "#0d6efd",
              colorText: "#1f2937",
              colorTextSecondary: "#6b7280",
              colorBackground: "#ffffff",
              colorDanger: "#dc2626",
              borderRadius: "7px",
              fontSizeBase: "13px",
              spacingUnit: "4px",
            },

            rules: {
              ".Input": {
                border: "1px solid #d9dde5",
                boxShadow: "none",
                padding: "10px 12px",
              },

              ".Input:focus": {
                border: "1px solid #0d6efd",
                boxShadow: "0 0 0 2px rgba(13, 110, 253, 0.10)",
              },

              ".Label": {
                color: "#4b5563",
                fontSize: "12px",
                fontWeight: "600",
              },
            },
          },
        });

        paymentElement.current = elements.current.create("payment", {
          layout: "tabs",
        });

        paymentElement.current.mount(mountRef.current);

        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(errorMessage(err));
          setLoading(false);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;

      paymentElement.current?.destroy();
    };
  }, []);

  const submit = async (event) => {
    event.preventDefault();

    if (!stripe.current || !elements.current) {
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const result = await stripe.current.confirmSetup({
        elements: elements.current,
        redirect: "if_required",
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      const intentId = result.setupIntent?.id || setupIntentId.current;

      const { data } = await api.post(`${ROOT}/payment-methods/confirm/`, {
        setup_intent_id: intentId,
      });

      onSaved(data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className="account-payment-setup"
      onSubmit={submit}
      style={{
        marginTop: "14px",
        marginBottom: "18px",
        padding: "16px",
        border: "1px solid #e4e7ec",
        borderRadius: "10px",
        background: "#f8fafc",
      }}
    >
      <div
        className="d-flex align-items-start justify-content-between"
        style={{
          gap: "12px",
          marginBottom: "14px",
        }}
      >
        <div>
          <h4
            style={{
              margin: "0 0 3px",
              fontSize: "15px",
              fontWeight: "700",
            }}
          >
            Add payment method
          </h4>

          <p className="account-muted" style={{ margin: 0 }}>
            Add a card or US bank account for subscriptions and AI credit purchases.
          </p>
        </div>

        <div
          className="d-flex align-items-center"
          style={{
            gap: "5px",
            padding: "4px 8px",
            border: "1px solid #d1fadf",
            borderRadius: "20px",
            background: "#ecfdf3",
            color: "#027a48",
            fontSize: "11px",
            fontWeight: "600",
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: "#12b76a",
            }}
          />
          Secure payment
        </div>
      </div>

      <Alert message={error} />

      <div
        style={{
          padding: "14px",
          border: "1px solid #e4e7ec",
          borderRadius: "8px",
          background: "#ffffff",
        }}
      >
        {loading && (
          <div
            className="account-muted"
            style={{
              padding: "8px 0",
              fontSize: "12px",
            }}
          >
            Preparing secure payment form…
          </div>
        )}

        <div ref={mountRef} />
      </div>

      <div
        className="d-flex align-items-center justify-content-between"
        style={{
          marginTop: "14px",
          gap: "12px",
        }}
      >
        <span
          className="account-muted"
          style={{
            fontSize: "11px",
          }}
        >
          Payment information is securely handled by Stripe.
        </span>

        <div
          className="account-actions"
          style={{
            marginTop: 0,
          }}
        >
          <button type="button" className="btn btn-light" disabled={submitting} onClick={onCancel}>
            Cancel
          </button>

          <button type="submit" className="btn btn-primary" disabled={loading || submitting}>
            {submitting ? "Saving…" : "Save payment method"}
          </button>
        </div>
      </div>
    </form>
  );
}

function Account() {
  const [section, setSection] = useState("subscription");

  const [subscription, setSubscription] = useState(null);

  const [methods, setMethods] = useState([]);

  const [invoices, setInvoices] = useState(null);

  const [credits, setCredits] = useState(null);

  const [featureCost, setFeatureCost] = useState(null);

  const [assignments, setAssignments] = useState(null);

  const [slotCount, setSlotCount] = useState(0);

  const [blocks, setBlocks] = useState(1);

  const [showPayment, setShowPayment] = useState(false);

  const [pendingUpgrade, setPendingUpgrade] = useState(false);

  const [downgradeScheduled, setDowngradeScheduled] = useState(false);

  const [purchase, setPurchase] = useState(null);

  const [busy, setBusy] = useState("");

  const [error, setError] = useState("");

  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setError("");

    try {
      const [sub, payment, invoice, credit] = await Promise.all([
        api.get(`${ROOT}/subscription/`),

        api.get(`${ROOT}/payment-methods/`),

        api.get(`${ROOT}/invoices/?page=1&page_size=250`),

        api.get(`${ROOT}/ai-credits/?page=1&page_size=250`),
      ]);

      setSubscription(sub.data);

      setSlotCount(sub.data.slot_capacity || 0);

      setMethods(payment.data.payment_methods || []);

      setInvoices(invoice.data);

      setCredits(credit.data);

      try {
        const { data } = await api.get(`${ROOT}/ai-credits/feature-cost/`, {
          params: {
            feature_key: "medical_chronology",
            units: 100,
          },
        });

        setFeatureCost(data);
      } catch {
        // The credits balance and purchase controls remain usable if pricing is unavailable.
        setFeatureCost(null);
      }

      if (sub.data.plan === "paid") {
        try {
          const { data } = await api.get(`${ROOT}/subscription/slot-assignments/`);

          setAssignments(data);
        } catch (err) {
          if (err.response?.status !== 403) {
            setError(errorMessage(err));
          }
        }
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!purchase) {
      return undefined;
    }

    const startedAt = new Date(purchase.startedAt).getTime() - 60_000;

    let attempts = 0;

    const poll = window.setInterval(async () => {
      attempts += 1;

      try {
        const { data } = await api.get(`${ROOT}/ai-credits/?page=1&page_size=25`);

        setCredits(data);

        const reflected =
          data.balance !== purchase.initialBalance ||
          data.results?.some(
            (entry) =>
              entry.entry_type === "credit" && new Date(entry.created_at).getTime() >= startedAt
          );

        if (reflected) {
          setPurchase(null);

          setNotice("Your AI credits are now available.");
        } else if (attempts >= 20) {
          setPurchase(null);

          setNotice(
            "The purchase is still processing. Refresh later to see webhook-confirmed credits."
          );
        }
      } catch {
        // retain last confirmed balance
      }
    }, 3000);

    return () => window.clearInterval(poll);
  }, [purchase]);

  const mutate = async (key, request, onSuccess) => {
    setBusy(key);
    setError("");
    setNotice("");

    try {
      const response = await request();

      onSuccess(response.data, response);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const upgrade = (skipPaymentGate = false) => {
    if (!skipPaymentGate && subscription?.has_usable_payment_method === false) {
      setPendingUpgrade(true);
      setShowPayment(true);
      setSection("payments");

      return;
    }

    mutate(
      "upgrade",

      () =>
        api.post(`${ROOT}/subscription/`, {
          plan: "paid",
        }),

      (data) => {
        setSubscription(data);

        setSlotCount(data.slot_capacity || 0);

        setNotice("Your firm is now on the paid plan.");

        load();
      }
    );
  };

  const changeSection = useCallback((id) => {
    setSection(id);

    if (id !== "payments") {
      setShowPayment((current) => {
        if (current) {
          setPendingUpgrade(false);
        }

        return false;
      });
    }
  }, []);
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);

  const mainTabs = useMemo(
    () =>
      [
        {
          id: "subscription",
          label: "Subscription",
        },
        {
          id: "payments",
          label: "Payment Methods",
        },
        {
          id: "invoices",
          label: "Invoices",
        },
        {
          id: "credits",
          label: "AI Credits",
        },
      ].map((tab) => ({
        ...tab,

        className: "tab-trapezium-cycles-corners",

        background: "var(--primary-60)",

        activeColor: "white",

        leftHand: false,

        onClick: () => changeSection(tab.id),
      })),
    [changeSection]
  );

  const primary = useMemo(
    () => methods.find((method) => method.role === "primary" && method.status === "active"),
    [methods]
  );

  const blocksValid = Number.isInteger(blocks) && blocks >= 1 && blocks <= 100;

  return (
    <div className="account-page">
      <div className="d-flex">
        <SmartResponsiveTabsContainer
          HandlePage={section}
          tabsData={mainTabs}
          viewTab={[]}
          addTab={[]}
          photoTab={[]}
          viewType={""}
          right={10}
        />
      </div>

      <Alert message={error} />

      <Alert message={notice} tone="success" />

      {!subscription && !error && <div className="account-card">Loading account details…</div>}

      {section === "subscription" && subscription && (
        <div className="account-stack">
          <div className="account-card">
            <div className="subscription-card-header">
              <div className="subscription-header-left">
                <div className="subscription-header-icon">$</div>

                <div>
                  <div className="subscription-title-row">
                    <h3>Subscription</h3>

                    <span
                      className={`subscription-plan-badge ${
                        subscription.plan === "paid"
                          ? "subscription-plan-paid"
                          : "subscription-plan-free"
                      }`}
                    >
                      <span className="subscription-plan-dot" />

                      {subscription.plan === "paid" ? "Paid Plan" : "Free Plan"}
                    </span>
                  </div>

                  <p className="account-muted subscription-description">
                    Manage your firm plan, billing, and user capacity.
                  </p>
                </div>
              </div>
            </div>

            {downgradeScheduled && (
              <div className="subscription-downgrade-notice">
                <div className="subscription-notice-icon">!</div>

                <div>
                  <strong>Downgrade scheduled</strong>

                  <p>
                    Your paid features will remain active until the end of the current billing
                    period.
                  </p>
                </div>
              </div>
            )}

            <div className="account-metrics">
              <div>
                <span>User slots</span>

                <strong>{subscription.slot_capacity}</strong>
              </div>

              <div>
                <span>Occupied</span>

                <strong>{subscription.occupied_slots}</strong>
              </div>

              <div>
                <span>Available</span>

                <strong>{subscription.free_slots}</strong>
              </div>

              <div>
                <span>Next billing date</span>

                <strong>{date(subscription.next_billing_date)}</strong>
              </div>
            </div>

            {subscription.plan === "free" ? (
              <div className="account-upgrade">
                <h4>Upgrade to Paid</h4>

                <div className="account-price-row">
                  <span>
                    {subscription.upgrade_slot_count} users ×{" "}
                    {money(subscription.user_monthly_rate)} / month
                  </span>

                  <strong>
                    {money(
                      Number(subscription.upgrade_slot_count) *
                        Number(subscription.user_monthly_rate)
                    )}{" "}
                    monthly
                  </strong>
                </div>

                <div className="account-price-row">
                  <span>Estimated charge today</span>

                  <strong>{money(subscription.upgrade_proration_preview)}</strong>
                </div>

                {subscription.has_usable_payment_method === null && (
                  <Alert
                    tone="warning"
                    message="Payment method availability could not be verified. You can retry the upgrade without changing local state."
                  />
                )}

                <button
                  className="btn btn-primary height-25 d-flex align-items-center justify-content-center px-2"
                  disabled={busy === "upgrade"}
                  onClick={() => upgrade()}
                >
                  {busy === "upgrade" ? "Upgrading…" : "Upgrade to Paid"}
                </button>
              </div>
            ) : (
              <div className="account-actions">
                <button
                  className="btn btn-danger height-25 d-flex align-items-center justify-content-center px-2"
                  disabled={busy === "downgrade" || downgradeScheduled}
                  onClick={() =>
                    mutate(
                      "downgrade",

                      () =>
                        api.post(`${ROOT}/subscription/`, {
                          plan: "free",
                        }),

                      (data) => {
                        if (data.effective === "period_end") {
                          setDowngradeScheduled(true);
                        } else {
                          setSubscription(data);
                        }

                        setNotice(data.detail || "Plan updated.");
                      }
                    )
                  }
                >
                  Downgrade to Free Plan
                </button>
              </div>
            )}
          </div>

          {subscription.plan === "paid" && (
            <div>
              <h3>User Slots</h3>

              <div className="account-inline-form m-b-5">
                <input
                  type="number"
                  className="height-25 d-flex align-items-center justify-content-center form-control"
                  style={{
                    width: "100px",
                    maxWidth: "100px",
                  }}
                  min={subscription.occupied_slots}
                  value={slotCount}
                  onChange={(e) => setSlotCount(Number(e.target.value))}
                />

                <button
                  className="btn btn-primary height-25 d-flex align-items-center justify-content-center"
                  disabled={
                    !Number.isInteger(slotCount) ||
                    slotCount < subscription.occupied_slots ||
                    slotCount === subscription.slot_capacity ||
                    busy === "slots"
                  }
                  onClick={() =>
                    mutate(
                      "slots",

                      () =>
                        api.post(`${ROOT}/subscription/slots/`, {
                          slot_count: slotCount,
                        }),

                      (data) => {
                        setSubscription(data);

                        setSlotCount(data.slot_capacity);

                        setNotice("Slot capacity updated.");

                        load();
                      }
                    )
                  }
                >
                  Update slots
                </button>
              </div>

              {Number.isInteger(slotCount) && slotCount < subscription.occupied_slots && (
                <p className="account-error-text">
                  At least {subscription.occupied_slots} occupied slots are required.
                </p>
              )}

              {assignments?.users?.length > 0 && (
                <table
                  className="table table-borderless table-striped table-treatment has-specialty-icon has-height-25 block-table"
                  id="slot-assignments-table"
                >
                  <thead>
                    <tr id="tb-header">
                      <th
                        style={{
                          minWidth: "25px",
                          maxWidth: "25px",
                          width: "25px",
                        }}
                      />

                      <th className="text-uppercase td-autosize text-center">User</th>
                      <th className="text-uppercase td-autosize text-center">Email</th>
                      {/* <th /> */}
                      <th className="text-uppercase td-autosize text-center">Slot</th>
                      <th className="text-uppercase td-autosize text-center">Created At</th>

                      <th className="" />
                    </tr>
                  </thead>

                  <tbody>
                    {assignments.users.map((user, idx) => (
                      <tr
                        key={user.attorney_staff_id}
                        style={{
                          height: "35px",
                        }}
                      >
                        <td
                          className="td-autosize text-center"
                          style={{
                            color: "var(--primary-25)",
                          }}
                        >
                          {idx + 1}
                        </td>

                        <td
                          className="td-autosize text-left"
                          id="padding-left-right"
                          style={{
                            fontWeight: "600",
                            textAlign: "left",
                          }}
                        >
                          <UserChatBubble
                            userId={user?.user?.id}
                            first_name={user?.user?.first_name ?? ""}
                            last_name={user?.user?.last_name ?? ""}
                            profilePic={user?.user?.profile_pic_29p ?? ""}
                            is_active={user?.user?.is_active}
                            width={21}
                            isActive={userOnlineStatus?.[user?.user?.id]?.isOnline ?? false}
                          />
                        </td>
                        <td
                          className="td-autosize text-left"
                          id="padding-left-right"
                          style={{
                            fontWeight: "600",
                            textAlign: "left",
                          }}
                        >
                          {user?.user?.email}
                        </td>
                        {/* <td /> */}
                        <td className="td-autosize text-center" id="padding-left-right">
                          {user.assigned ? "Assigned" : "Unassigned"}
                        </td>
                        <td
                          className="td-autosize text-left"
                          id="padding-left-right"
                          style={{
                            fontWeight: "600",
                            textAlign: "left",
                          }}
                        >
                          {formatDate(user?.created_at)}
                        </td>
                        <td className=" text-center">
                          <div className="d-flex align-items-center height-25 justify-content-end pe-2">
                            <button
                              className="btn btn-secondary height-25 d-flex align-items-center btn-hover-green"
                              disabled={
                                busy === `user-${user.attorney_staff_id}` ||
                                (!user.assigned &&
                                  assignments.slot_capacity <= assignments.occupied_slots)
                              }
                              onClick={() =>
                                mutate(
                                  `user-${user.attorney_staff_id}`,

                                  () =>
                                    api.post(`${ROOT}/subscription/slot-assignments/`, {
                                      attorney_staff_id: user.attorney_staff_id,

                                      action: user.assigned ? "unassign" : "assign",
                                    }),

                                  setAssignments
                                )
                              }
                            >
                              {user.assigned ? "Unassign" : "Assign"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {section === "payments" && (
        <div className="account-stack p-t-5">
          <div className="account-card">
            <div
              className="account-card-heading"
              style={{
                marginBottom: "4px",
              }}
            >
              <div>
                <h3>Payment methods</h3>

                <p className="account-muted">
                  Manage the primary and backup methods used for subscriptions and AI credits.
                </p>
              </div>

              {!showPayment && (
                <button
                  className="btn btn-primary d-flex align-items-center justify-content-center gap-1 height-25 px-2"
                  onClick={() => setShowPayment(true)}
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  <span>
                    <img src={addIconGold} className="ic ic-15 d-flex align-items-center" />
                  </span>{" "}
                  Payment Method
                </button>
              )}
            </div>

            {showPayment && (
              <PaymentSetup
                onCancel={() => {
                  setShowPayment(false);

                  setPendingUpgrade(false);
                }}
                onSaved={async (method) => {
                  setShowPayment(false);

                  setMethods((current) => [
                    method,

                    ...current.filter((item) => item.id !== method.id),
                  ]);

                  setNotice(
                    method.status === "pending_verification"
                      ? "Bank account saved and pending verification."
                      : "Payment method saved."
                  );

                  try {
                    const { data } = await api.get(`${ROOT}/payment-methods/`);

                    setMethods(data.payment_methods || []);
                  } catch {
                    // keep the returned method
                  }

                  if (pendingUpgrade && method.status === "active") {
                    setPendingUpgrade(false);

                    setSection("subscription");

                    upgrade(true);
                  }
                }}
              />
            )}

            <div
              style={{
                marginTop: showPayment ? "4px" : "16px",
              }}
            >
              {methods.length === 0 ? (
                <div
                  className="d-flex flex-column align-items-center justify-content-center"
                  style={{
                    minHeight: "150px",
                    padding: "24px",
                    border: "1px dashed #d9dde5",
                    borderRadius: "10px",
                    background: "#fafbfc",
                    textAlign: "center",
                  }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: "40px",
                      height: "40px",
                      marginBottom: "8px",
                      borderRadius: "10px",
                      background: "#eef4ff",
                      color: "#0d6efd",
                      fontSize: "20px",
                      fontWeight: "700",
                    }}
                  >
                    $
                  </div>

                  <strong
                    style={{
                      fontSize: "13px",
                      color: "#344054",
                    }}
                  >
                    No payment methods yet
                  </strong>

                  <span
                    className="account-muted"
                    style={{
                      marginTop: "3px",
                    }}
                  >
                    Add a card or US bank account to get started.
                  </span>
                </div>
              ) : (
                <div
                  className="d-flex flex-column"
                  style={{
                    gap: "10px",
                  }}
                >
                  {methods.map((method) => {
                    const isPrimary = method.role === "primary";

                    const isPending = method.status === "pending_verification";

                    const isBusy = busy === `method-${method.id}`;

                    const nextRole = isPrimary ? "backup" : "primary";

                    const deletionBlocked =
                      subscription?.plan === "paid" && isPrimary && method.status === "active";

                    const methodName =
                      method.method_type === "card"
                        ? method.brand || "Card"
                        : method.bank_name || "US bank account";

                    const roleLabel = isPrimary ? "Primary" : "Backup";

                    const roleBackground = isPrimary ? "#ecfdf3" : "#f2f4f7";

                    const roleColor = isPrimary ? "#027a48" : "#475467";

                    const roleBorder = isPrimary ? "#abefc6" : "#e4e7ec";

                    const hasExpiry = method.exp_month && method.exp_year;

                    return (
                      <div
                        key={method.id}
                        className="d-flex align-items-center justify-content-between"
                        style={{
                          minHeight: "74px",
                          padding: "12px 14px",
                          gap: "16px",
                          border: `1px solid ${isPrimary ? "#b9d7ff" : "#e4e7ec"}`,
                          borderRadius: "10px",
                          background: isPrimary ? "#fbfdff" : "#ffffff",
                          boxShadow: isPrimary
                            ? "0 1px 3px rgba(13, 110, 253, 0.06)"
                            : "0 1px 2px rgba(16, 24, 40, 0.03)",
                        }}
                      >
                        <div
                          className="d-flex align-items-center"
                          style={{
                            minWidth: 0,
                            gap: "12px",
                          }}
                        >
                          <div
                            className="d-flex align-items-center justify-content-center"
                            style={{
                              width: "46px",
                              height: "32px",
                              flex: "0 0 46px",
                              border: "1px solid #e4e7ec",
                              borderRadius: "6px",
                              background: "#ffffff",
                              color: "#344054",
                              fontSize: "10px",
                              fontWeight: "800",
                              textTransform: "uppercase",
                              letterSpacing: "0.3px",
                            }}
                          >
                            {method.method_type === "card"
                              ? String(method.brand || "Card").slice(0, 6)
                              : "BANK"}
                          </div>

                          <div
                            style={{
                              minWidth: 0,
                            }}
                          >
                            <div
                              className="d-flex align-items-center flex-wrap"
                              style={{
                                gap: "7px",
                                marginBottom: "3px",
                              }}
                            >
                              <strong
                                style={{
                                  color: "#1d2939",
                                  fontSize: "13px",
                                  fontWeight: "700",
                                }}
                              >
                                {methodName} •••• {method.last4}
                              </strong>

                              {!isPending && (
                                <span
                                  style={{
                                    padding: "2px 7px",
                                    border: `1px solid ${roleBorder}`,
                                    borderRadius: "20px",
                                    background: roleBackground,
                                    color: roleColor,
                                    fontSize: "10px",
                                    fontWeight: "700",
                                    lineHeight: "16px",
                                  }}
                                >
                                  {roleLabel}
                                </span>
                              )}

                              {isPending && (
                                <span
                                  style={{
                                    padding: "2px 7px",
                                    border: "1px solid #fedf89",
                                    borderRadius: "20px",
                                    background: "#fffaeb",
                                    color: "#b54708",
                                    fontSize: "10px",
                                    fontWeight: "700",
                                    lineHeight: "16px",
                                  }}
                                >
                                  Pending verification
                                </span>
                              )}
                            </div>

                            <div
                              className="d-flex align-items-center flex-wrap account-muted"
                              style={{
                                gap: "8px",
                                fontSize: "11px",
                              }}
                            >
                              <span>
                                {method.method_type === "card" ? "Card" : "US bank account"}
                              </span>

                              {hasExpiry && (
                                <>
                                  <span>•</span>

                                  <span>
                                    Expires {String(method.exp_month).padStart(2, "0")}/
                                    {String(method.exp_year).slice(-2)}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div
                          className="d-flex align-items-center justify-content-end"
                          style={{
                            gap: "7px",
                            flexShrink: 0,
                          }}
                        >
                          <button
                            className="btn btn-light btn-sm"
                            disabled={method.status !== "active" || isBusy}
                            onClick={() =>
                              mutate(
                                `method-${method.id}`,

                                () =>
                                  api.post(`${ROOT}/payment-methods/${method.id}/`, {
                                    role: nextRole,
                                  }),

                                (data) => setMethods(data.payment_methods || [])
                              )
                            }
                          >
                            {isBusy ? "Updating…" : isPrimary ? "Make Backup" : "Make Primary"}
                          </button>

                          <button
                            className="btn btn-danger btn-sm"
                            style={{
                              color: deletionBlocked ? undefined : "#b42318",
                            }}
                            disabled={isBusy || deletionBlocked}
                            title={
                              deletionBlocked
                                ? "Set another payment method as primary before deleting this one."
                                : "Delete payment method"
                            }
                            onClick={() =>
                              mutate(
                                `method-${method.id}`,

                                () => api.delete(`${ROOT}/payment-methods/${method.id}/`),

                                (data) => setMethods(data.payment_methods || [])
                              )
                            }
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {section === "invoices" && (
        <div>
          <h3>Invoices</h3>

          <table
            className="table table-borderless table-striped table-treatment has-specialty-icon has-height-25 block-table"
            id="invoices-table"
          >
            <thead>
              <tr id="tb-header">
                <th
                  style={{
                    maxWidth: "25px",
                    minWidth: "25px",
                    width: "25px",
                  }}
                />

                <th className="text-uppercase td-autosize text-center">Invoice</th>
                <th />
                <th className="text-uppercase td-autosize text-center">Date</th>
                <th />
                <th className="text-uppercase td-autosize text-center">Status</th>
                <th />
                <th className="text-uppercase td-autosize text-center">Amount</th>
                <th />
                <th className="text-uppercase td-autosize text-center">Links</th>
              </tr>
            </thead>

            <tbody>
              {invoices?.results?.map((invoice, idx) => (
                <tr
                  key={invoice.id}
                  style={{
                    height: "35px",
                  }}
                >
                  <td
                    className=" text-center"
                    style={{
                      color: "var(--primary-25)",
                    }}
                  >
                    {idx + 1}
                  </td>

                  <td
                    className=" text-center"
                    id="padding-left-right"
                    style={{
                      fontWeight: "600",
                    }}
                  >
                    {invoice.number || "—"}
                  </td>
                  <td />
                  <td className=" text-center" id="padding-left-right">
                    {date(invoice.created_at)}
                  </td>
                  <td />
                  <td className=" text-center" id="padding-left-right">
                    {invoice.status}
                  </td>
                  <td />
                  <td className=" text-center" id="padding-left-right">
                    {money(invoice.amount, invoice.currency)}
                  </td>
                  <td />
                  <td className=" text-center" id="padding-left-right">
                    {invoice.hosted_invoice_url && (
                      <a href={invoice.hosted_invoice_url} target="_blank" rel="noreferrer">
                        View
                      </a>
                    )}{" "}
                    {invoice.pdf_url && (
                      <a href={invoice.pdf_url} target="_blank" rel="noreferrer">
                        PDF
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {invoices?.results?.length === 0 && <p className="account-muted">No invoices yet.</p>}
        </div>
      )}

      {section === "credits" && credits && (
        <div className="account-stack p-t-5">
          <div className="ai-credits-card">
            <div className="ai-credits-header">
              <div>
                <div className="ai-credits-title-row">
                  <h3>AI Credits</h3>

                  <span className="ai-credits-status">
                    <span className="ai-credits-status-dot" />
                    Available
                  </span>
                </div>

                <p className="account-muted">
                  Credits are automatically updated after payment confirmation.
                </p>
              </div>

              <div className="ai-credits-balance pe-1">
                <span className="ai-credits-balance-label">Available balance</span>

                <div className="ai-credits-balance-value">
                  <strong>{credits.balance}</strong>

                  <span>credits</span>
                </div>
              </div>
            </div>

            {featureCost && (
              <div className="ai-feature-cost">
                <div className="ai-feature-cost-heading">
                  <div>
                    <h4>Medical chronology</h4>

                    <p className="account-muted">
                      Estimated cost for {featureCost.units.toLocaleString()} pages
                    </p>
                  </div>

                  <span
                    className={`ai-feature-cost-availability ${
                      featureCost.sufficient ? "is-sufficient" : "is-insufficient"
                    }`}
                  >
                    {featureCost.sufficient ? "Sufficient balance" : "Insufficient balance"}
                  </span>
                </div>

                <div className="ai-feature-cost-grid">
                  <div>
                    <span>Credits charged</span>
                    <strong>
                      {featureCost.is_free ? "Free" : featureCost.credits_charged.toLocaleString()}
                    </strong>
                  </div>

                  <div>
                    <span>Estimated cost</span>
                    <strong>
                      {featureCost.is_free ? "Free" : money(featureCost.estimated_usd)}
                    </strong>
                  </div>

                  <div>
                    <span>Rate</span>
                    <strong>
                      {featureCost.credits_per_unit} credit / {featureCost.unit_size} pages
                    </strong>
                  </div>

                  <div>
                    <span>Current balance</span>
                    <strong>{featureCost.balance.toLocaleString()} credits</strong>
                  </div>
                </div>

                {featureCost.description && (
                  <p className="ai-feature-cost-description">{featureCost.description}</p>
                )}
              </div>
            )}

            <div>
              <div className="ai-credits-purchase-heading">
                <div>
                  <h4>Purchase credits</h4>

                  <p className="account-muted">Add additional credits to your account.</p>
                </div>

                <div className="ai-credits-block-price">
                  {money(credits.block_price_usd)}

                  <span> / {credits.block_size} credits</span>
                </div>
              </div>

              <div className="ai-credits-purchase-grid">
                <div
                  style={{
                    width: "200px",
                  }}
                  className="ai-credits-field"
                >
                  <label htmlFor="credit-blocks" className="m-b-0">
                    Number of blocks
                  </label>

                  <div className="ai-credits-input-wrapper">
                    <button
                      type="button"
                      className="ai-credits-stepper"
                      disabled={blocks <= 1}
                      onClick={() => setBlocks((prev) => Math.max(1, prev - 1))}
                    >
                      -
                    </button>

                    <input
                      id="credit-blocks"
                      type="number"
                      min="1"
                      max="100"
                      style={{
                        width: "100px",
                      }}
                      value={blocks}
                      onChange={(e) => setBlocks(Number(e.target.value))}
                    />

                    <button
                      type="button"
                      className="ai-credits-stepper"
                      disabled={blocks >= 100}
                      onClick={() => setBlocks((prev) => Math.min(100, prev + 1))}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="ai-credits-summary-item">
                  <span className="ai-credits-summary-label">Credits you will receive</span>

                  <strong className="height-25">
                    {blocksValid ? (blocks * credits.block_size).toLocaleString() : "—"}
                  </strong>
                </div>

                <div className="ai-credits-summary-item ai-credits-total">
                  <span className="ai-credits-summary-label">Total</span>

                  <strong className="height-25">
                    {blocksValid ? money(blocks * Number(credits.block_price_usd)) : "—"}
                  </strong>
                </div>

                <button
                  className="btn btn-primary ai-credits-buy-button"
                  disabled={!primary || !blocksValid || busy === "purchase" || Boolean(purchase)}
                  onClick={() =>
                    mutate(
                      "purchase",

                      () =>
                        api.post(`${ROOT}/ai-credits/purchase/`, {
                          blocks,
                        }),

                      (data, response) => {
                        if (response.status === 202) {
                          setPurchase({
                            initialBalance: credits.balance,

                            startedAt: new Date().toISOString(),

                            id: data.payment_intent_id,
                          });

                          setNotice(data.detail);
                        }
                      }
                    )
                  }
                >
                  {purchase
                    ? "Processing payment…"
                    : busy === "purchase"
                      ? "Submitting…"
                      : `Buy ${blocksValid ? `${blocks * credits.block_size} credits` : "credits"}`}
                </button>
              </div>
            </div>

            {purchase && (
              <div className="ai-credits-message ai-credits-message-info">
                <span className="ai-credits-spinner" />

                <div>
                  <strong>Payment is being confirmed</strong>

                  <span>Your balance will update automatically once confirmation is received.</span>
                </div>
              </div>
            )}

            {!primary && (
              <div className="ai-credits-message ai-credits-message-error">
                <div>
                  <strong>Payment method required</strong>

                  <span>Add an active primary payment method before purchasing AI credits.</span>
                </div>
              </div>
            )}
          </div>

          <div>
            <h3>Credit History</h3>

            <table
              className="table table-borderless table-striped table-treatment has-specialty-icon has-height-25 block-table"
              id="credit-history-table"
            >
              <thead>
                <tr id="tb-header">
                  <th
                    style={{
                      maxWidth: "25px",
                      minWidth: "25px",
                      width: "25px",
                    }}
                  />

                  <th className="text-uppercase td-autosize text-center">Description</th>
                  <th />
                  <th className="text-uppercase td-autosize text-center">Date</th>
                  <th />
                  <th className="text-uppercase td-autosize text-center">Credits</th>
                  <th />
                  <th className="text-uppercas td-autosize text-center">Balance</th>
                </tr>
              </thead>

              <tbody>
                {credits.results?.map((entry, idx) => (
                  <tr
                    key={entry.id}
                    style={{
                      height: "35px",
                    }}
                  >
                    <td
                      className="text-center"
                      style={{
                        color: "var(--primary-25)",
                      }}
                    >
                      {idx + 1}
                    </td>

                    <td
                      className="td-autosize text-center"
                      id="padding-left-right"
                      style={{
                        fontWeight: "600",
                      }}
                    >
                      {entry.description}
                    </td>
                    <td />
                    <td className="td-autosize text-center" id="padding-left-right">
                      {date(entry.created_at)}
                    </td>
                    <td />
                    <td
                      className={`td-autosize text-center ${
                        entry.entry_type === "credit" ? "account-positive" : ""
                      }`}
                      id="padding-left-right"
                      style={{
                        fontWeight: "600",
                      }}
                    >
                      {entry.entry_type === "credit" ? "+" : "−"}

                      {entry.credits}
                    </td>
                    <td />
                    <td className="td-autosize text-center" id="padding-left-right">
                      {entry.balance}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {credits.results?.length === 0 && (
              <p className="account-muted">No credit activity yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Account;
